import { createRouter, createWebHistory, RouteRecordRaw, RouteLocationNormalized } from "vue-router";
import useAuth from "@/modules/oauth/composables/useAuth";
import fedidService from "@/modules/oauth/services/FedidService";
import useHda from "@/composables/useHda";
import { ConfirmSwal } from "@/tools";
import i18nConfig from "@/modules/language/configuration/i18nConfig";
import { ROLE_APPS, ROLE_NAMES } from "@/modules/oauth/constants/roles";

import hdaRoutes from "@/modules/hdaModule/router";
import planningRoutes from "@/modules/planningModule/router";
import configurationRoutes from "@/modules/configurationModule/router/index";
import visitorsRoutes from "@/modules/visitorsModule/router";
import delaysRoutes from "@/modules/delaysModule/router";
import bookingsRoutes from "@/modules/bookingsModule/router";

function resolveRootRedirect(): { name: string } {
  const { loginState, accessToken } = useAuth();
  if (!loginState.value || !accessToken.value) {
    return { name: "noRole" };
  }
  const flags = getRoleFlags();
  if (flags.dassistSuperAdmin || flags.visitorsAdmin || flags.visitors) {
    return { name: "visitors" };
  }
  if (flags.delaysAdmin) {
    return { name: "delays" };
  }
  if (flags.hdaOperator || flags.hdaSuperAdmin || flags.hdaManager || flags.hdaEconomic) {
    return { name: "collaborator" };
  }
  return { name: "noRole" };
}

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: () => resolveRootRedirect(),
  },
  hdaRoutes,
  configurationRoutes,
  planningRoutes,
  visitorsRoutes,
  bookingsRoutes,
  delaysRoutes,
  {
    path: "/no-role",
    name: "noRole",
    component: () => import(/* webpackChunkName: "no-role" */ "@/modules/oauth/views/NoRoleView.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: { name: "collaborator" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

interface RoleFlags {
  hdaOperator: boolean;
  hdaSuperAdmin: boolean;
  hdaManager: boolean;
  hdaEconomic: boolean;

  visitors: boolean;
  visitorsAdmin: boolean;
  dassistSuperAdmin: boolean;

  delaysAdmin: boolean;
}

type RoleGuard = (roles: RoleFlags) => boolean;

const ONLY_HDA_SUPERADMIN: RoleGuard = (roles) => roles.hdaSuperAdmin;
const HDA_SUPERADMIN_OR_MANAGER: RoleGuard = (roles) => roles.hdaSuperAdmin || roles.hdaManager;
const ECONOMIC_OR_HDA_SUPERADMIN: RoleGuard = (roles) => roles.hdaEconomic || roles.hdaSuperAdmin;

const CAN_ACCESS_CONFIGURATION: RoleGuard = (roles) =>
  roles.hdaSuperAdmin || roles.visitorsAdmin || roles.dassistSuperAdmin || roles.delaysAdmin;

const ANY_VISITOR_ROLE: RoleGuard = (roles) => roles.visitors || roles.visitorsAdmin || roles.dassistSuperAdmin;
const VISITORS_ADMIN_OR_SUPER: RoleGuard = (roles) => roles.visitorsAdmin || roles.dassistSuperAdmin;

const DELAYS_ACCESS: RoleGuard = (roles) => roles.visitors || roles.delaysAdmin || roles.dassistSuperAdmin;
const DELAYS_ADMIN_ACCESS: RoleGuard = (roles) => roles.delaysAdmin || roles.dassistSuperAdmin;

const ONLY_DASSIST_SUPERADMIN: RoleGuard = (roles) => roles.dassistSuperAdmin;

const PROTECTED_ROUTES: Record<string, RoleGuard> = {
  explotation: HDA_SUPERADMIN_OR_MANAGER,
  individualDetails: HDA_SUPERADMIN_OR_MANAGER,
  economic: ECONOMIC_OR_HDA_SUPERADMIN,
  users: HDA_SUPERADMIN_OR_MANAGER,
  planning: HDA_SUPERADMIN_OR_MANAGER,
  planningList: HDA_SUPERADMIN_OR_MANAGER,
  bookingsAdminHome: HDA_SUPERADMIN_OR_MANAGER,
  adminBookings: HDA_SUPERADMIN_OR_MANAGER,
  adminBookingDetail: HDA_SUPERADMIN_OR_MANAGER,
  adminInventory: HDA_SUPERADMIN_OR_MANAGER,
  adminProductUnits: HDA_SUPERADMIN_OR_MANAGER,
  adminUnitDetail: HDA_SUPERADMIN_OR_MANAGER,
  adminUnitHistory: HDA_SUPERADMIN_OR_MANAGER,
  adminMaintenance: HDA_SUPERADMIN_OR_MANAGER,
  adminAnalytics: HDA_SUPERADMIN_OR_MANAGER,
};

function getRoleFlags(): RoleFlags {
  const { getRolesByAppName } = useAuth();

  const hdaRoles = getRolesByAppName(ROLE_APPS.HDA_ROLES) ?? [];
  const dassistRoles = getRolesByAppName(ROLE_APPS.DASSIST_ROLES) ?? [];

  const hdaRoleSet = new Set(hdaRoles.map((role) => role.role));
  const dassistRoleset = new Set(dassistRoles.map((role) => role.role));

  return {
    hdaOperator: hdaRoleSet.has(ROLE_NAMES.HDA_OPERATOR),
    hdaSuperAdmin: hdaRoleSet.has(ROLE_NAMES.HDA_SUPERADMIN),
    hdaManager: hdaRoleSet.has(ROLE_NAMES.WAREHOUSEMANAGER),
    hdaEconomic: hdaRoleSet.has(ROLE_NAMES.ECONOMIC),

    visitors: dassistRoleset.has(ROLE_NAMES.VISITORS),
    visitorsAdmin: dassistRoleset.has(ROLE_NAMES.VISITORS_ADMIN),
    dassistSuperAdmin: dassistRoleset.has(ROLE_NAMES.DASSIST_SUPERADMIN),
    delaysAdmin: dassistRoleset.has(ROLE_NAMES.DELAYS_ADMIN),
  };
}

function checkVisitorsRoute(routeName: string, flags: RoleFlags): boolean {
  if (routeName === "visitorsData") {
    return VISITORS_ADMIN_OR_SUPER(flags);
  }

  return ANY_VISITOR_ROLE(flags);
}

function checkDelaysRoute(routeName: string, flags: RoleFlags): boolean {
  if (routeName === "delaysData") {
    return DELAYS_ADMIN_ACCESS(flags);
  }

  return DELAYS_ACCESS(flags);
}

function checkConfigurationRoute(configParam: string, flags: RoleFlags): boolean {
  const canAccessConfiguration = CAN_ACCESS_CONFIGURATION(flags);

  if (!canAccessConfiguration) {
    return false;
  }

  if (!configParam) {
    return true;
  }

  const allowedConfigs: string[] = [];

  if (flags.hdaSuperAdmin) {
    allowedConfigs.push("sites", "categories", "collaborators", "teams");
  }

  if (flags.dassistSuperAdmin) {
    allowedConfigs.push(
      "visitorsCategories",
      "fields",
      "visitorsSites",
      "visitorsTeams",
      "leaders",
      "delayCenters",
      "reasonCategories",
      "bookingTypes",
      "bookingCategories",
      "bookingSubcategories",
      "bookingsCenters",
    );
  }
  if (flags.visitorsAdmin) {
    allowedConfigs.push("leaders", "visitorsTeams", "visitorsSites");
  }

  return allowedConfigs.includes(configParam);
}

function checkBookingsRoute(configParam: string, flags: RoleFlags): boolean {
  return ONLY_DASSIST_SUPERADMIN(flags);
}

function resolveProtectedRoute(to: RouteLocationNormalized): boolean | { name: string } {
  if (to.name === "noRole") {
    return true;
  }

  const flags = getRoleFlags();
  const routeName = to.name as string;

  if (to.path.startsWith("/visitors")) {
    return checkVisitorsRoute(routeName, flags) ? true : resolveRootRedirect();
  }

  if (to.path.startsWith("/delays")) {
    return checkDelaysRoute(routeName, flags) ? true : resolveRootRedirect();
  }

  if (to.path.startsWith("/configuration")) {
    const currentConfig = to.params.config as string;
    return checkConfigurationRoute(currentConfig, flags) ? true : resolveRootRedirect();
  }

  if (to.path.startsWith("/bookings")) {
    const currentConfig = to.params.config as string;
    return checkBookingsRoute(currentConfig, flags) ? true : resolveRootRedirect();
  }

  const guard = PROTECTED_ROUTES[routeName];
  if (guard && !guard(flags)) {
    return resolveRootRedirect();
  }

  return true;
}

router.beforeEach((to, from) => {
  if (from.name === "collaborator") {
    const { thereAreChanges, setChanges } = useHda();
    const t = i18nConfig.global.t;

    if (thereAreChanges.value) {
      void ConfirmSwal(t("pending_save"), t("hours_pending_save_description"), "warning", t("ignore"), t("cancel")).then(
        ({ isConfirmed }) => {
          if (isConfirmed) {
            setChanges(false);
            void router.push(to);
          }
        }
      );
      return false;
    }
  }
  return true;
});

router.beforeEach((to) => resolveProtectedRoute(to));

router.beforeEach(() => {
  fedidService.revokeSession();
  return true;
});

router.afterEach((to) => {
  const parentRoute = to.matched[0];
  const titleKey = parentRoute?.meta?.title || to.path.split("/")[1];

  if (titleKey && typeof titleKey === "string") {
    if (titleKey === "HDA") {
      document.title = titleKey;
      return;
    }
    document.title = titleKey.charAt(0).toUpperCase() + titleKey.slice(1).toLowerCase();
  } else {
    document.title = "-";
  }
});

export default router;
